import { QueryClient } from '@tanstack/react-query';
import { QueryCache } from '@tanstack/react-query';
import { MutationCache } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import { tsRestFetchApi } from '@ts-rest/core';
import { getHttpErrorStatus } from '@socialgoodsoftware/components';
import { getHttpErrorCode } from '@socialgoodsoftware/components';
import { isMfaEnrollmentRequiredError } from '@socialgoodsoftware/components';
import { notifyUnauthenticated } from '@socialgoodsoftware/components';
import { readSessionHeaders } from '@socialgoodsoftware/components';
import { buildLoginRedirectForCurrentLocation } from './login-redirect';
import { notifyOrganizationDeactivated } from './organization-deactivated-signal';
import { AssignWindowLocation } from './redirect-allowlist';

import type { ReactNode } from 'react';
import type { ApiFetcherArgs } from '@ts-rest/core';
import type { AppRouter } from '@ts-rest/core';

const authPathPrefixes = ['/login', '/signup', '/forgot-password', '/reset-password', '/auth'];

export const enrollMfaPath = '/auth/enroll-mfa';

function shouldRetry(failureCount: number, error: unknown): boolean {
  // ts-rest throws `{ status, body }` for declared non-2xx. Never retry client errors
  // (including the guest /login getUser 401 probe) — retries are what turn one 401 into three.
  let retry = false;
  const status = getHttpErrorStatus(error);
  if (status !== null && status >= 400 && status < 500) {
    retry = false;
  } else if (failureCount < 2) {
    retry = true;
  }
  return retry;
}

function isOnAuthPath(): boolean {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return authPathPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function handleUnauthorized(): void {
  // Account is the auth host. Guest pages never probe /api/user; a 401 on a
  // protected page means a live session died and should broadcast logout.
  if (!isOnAuthPath()) {
    notifyUnauthenticated(buildLoginRedirectForCurrentLocation());
  }
}

function handleMfaEnrollmentRequired(): void {
  // Account host: SPA navigate to local enroll page (not product full-document reload).
  if (typeof window !== 'undefined' && window.location.pathname !== enrollMfaPath) {
    AssignWindowLocation(enrollMfaPath);
  }
}

export function isOrganizationDeactivatedError(error: unknown): boolean {
  let deactivated = false;
  if (getHttpErrorStatus(error) === 401 && getHttpErrorCode(error) === 'organizationDeactivated') {
    deactivated = true;
  }
  return deactivated;
}

export function handleQueryError(error: unknown): void {
  const status = getHttpErrorStatus(error);
  if (isOrganizationDeactivatedError(error)) {
    notifyOrganizationDeactivated();
  } else if (status === 401) {
    handleUnauthorized();
  } else if (isMfaEnrollmentRequiredError(error)) {
    handleMfaEnrollmentRequired();
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleQueryError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleQueryError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
      // Failed guest probes (401 on /login) must not re-fire on React StrictMode remount.
      retryOnMount: false,
    },
  },
});

async function sessionAwareApi(args: ApiFetcherArgs) {
  const response = await tsRestFetchApi(args);
  readSessionHeaders(response.headers);
  return response;
}

export function createContractClient<TContract extends AppRouter>(contract: TContract) {
  return initTsrReactQuery(contract, {
    baseUrl: '',
    credentials: 'same-origin',
    api: sessionAwareApi,
  });
}

export function featureQueryKey(
  feature: string,
  ...parts: ReadonlyArray<string | number>
): readonly [string, ...Array<string | number>] {
  return [feature, ...parts];
}

export function ApiProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
